import React, { useEffect, useRef, useState } from 'react';

import { ExpandMore } from '@mui/icons-material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    createTheme,
    Dialog,
    DialogContent,
} from '@mui/material';
import { createRoot } from 'react-dom/client';

import Button from '@components/Button';
import DialogCloseButton from '@components/DialogCloseButton';
import AnalysisFullMethodsContent from '@components/experiments/methods/modal/AnalysisFullMethodsContent';
import PipelineMethodsView, {
    isPipelineMethodsSupported,
} from '@components/experiments/pipelineMethods/PipelineMethodsView';
import LoadingMessage from '@components/LoadingMessage';
import PlutoDialogTitle from '@components/PlutoDialogTitle';
import PlutoMarkdownContentView from '@components/PlutoMarkdownContentView';

import useApi from '@hooks/useApi';
import useCopyToClipboard from '@hooks/useCopyToClipboard';
import useExperimentPlotListItems from '@hooks/useExperimentPlotListItems';

import { CopyIcon } from '@icons/custom/CopyIcon';

import AnnotationSet from '@models/Annotation';
import Experiment from '@models/Experiment';
import { PlotListItem } from '@models/Plot';

import Endpoints from '@services/Endpoints';

import Logger from '@util/Logger';

import PipelineMethodsContent from './PipelineMethodsContent';

const logger = Logger.make('ExperimentMethodsModal');

const theme = createTheme();

const handleResponseItem = (responseItem: any): string => {
    if (typeof responseItem === 'string') {
        return responseItem;
    }

    if (typeof responseItem === 'object' && responseItem !== null) {
        // Try to extract content from known fields
        const possibleFields = ['methods', 'text', 'content'];
        for (const field of possibleFields) {
            if (field in responseItem) {
                logger.debug(`Extracted ${field} from response object`);
                return responseItem[field];
            }
        }
        // Fallback to stringifying the object
        logger.debug('Stringified response object');
        return JSON.stringify(responseItem, null, 2);
    }

    return '';
};

const extractMethodsTextFromResponse = (data: any): string => {
    // Handle nested response format
    if (data.response?.response && Array.isArray(data.response.response) && data.response.response.length > 0) {
        const responseItem = data.response.response[0];
        logger.debug('Got nested array response from Gemini API:', {
            responseType: typeof responseItem,
            responseValue: responseItem,
        });
        return handleResponseItem(responseItem);
    }

    // Handle array response format
    if (data.response && Array.isArray(data.response) && data.response.length > 0) {
        const responseItem = data.response[0];
        logger.debug('Got array response from Gemini API:', {
            responseType: typeof responseItem,
            responseValue: responseItem,
        });

        return handleResponseItem(responseItem);
    }

    // Handle string response formats
    if (typeof data.response === 'string') return data.response;
    if (typeof data.text === 'string') return data.text;
    if (typeof data.content === 'string') return data.content;

    // Handle Gemini API v1 format
    if (data.candidates && Array.isArray(data.candidates) && data.candidates.length > 0) {
        const candidate = data.candidates[0];
        if (candidate.content?.parts && Array.isArray(candidate.content.parts)) {
            logger.debug('Extracted text from candidates.content.parts');
            return candidate.content.parts.map((part: { text?: string }) => part.text || '').join('\n');
        }
    }

    // Fallback error message
    logger.error('Invalid response format from Gemini API:', data);
    return 'Failed to parse response from Gemini API. Please try again.';
};

const styles = {
    accordion: {
        boxShadow: 'none',
        borderRadius: '10px !important',
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: theme.palette.grey[300], // Use specific grey shade
    },
    accordionSummary: {
        minHeight: 'auto !important',
        padding: theme.spacing(0, 3),
    },
    accordionDetails: {
        padding: theme.spacing(3),
        paddingTop: 0,
    },
};

type Props = {
    open: boolean;
    onClose: () => void;
    experiment: Experiment;
};

const ExperimentMethodsModal = ({ open = false, onClose, experiment }: Props) => {
    const generatedMethodsRef = useRef<HTMLDivElement>(null);
    const api = useApi();

    const { plotListItems, loading, error } = useExperimentPlotListItems({
        experimentId: experiment?.uuid,
        skip: !open,
    });

    const { copyText, highlightText, showCopySuccess } = useCopyToClipboard({
        ref: generatedMethodsRef,
        successTimeoutDuration: 3000,
    });
    const pipelineMethodsSupported = isPipelineMethodsSupported(experiment);

    const [generatingMethods, setGeneratingMethods] = useState(false);
    const [generatedMethods, setGeneratedMethods] = useState<string | null>(null);
    const [generationError, setGenerationError] = useState<string | null>(null);
    const [expandedAnalyses, setExpandedAnalyses] = useState<Record<string, boolean>>({});
    const [selectedAnalyses, setSelectedAnalyses] = useState<Record<string, boolean>>({});
    const [pipelineMethodsExpanded, setPipelineMethodsExpanded] = useState(pipelineMethodsSupported);
    const [pipelineMethodsSelected, setPipelineMethodsSelected] = useState(pipelineMethodsSupported);
    const [annotationSets, setAnnotationSets] = useState<AnnotationSet[]>([]);
    const [analysesRan, setAnalysesRan] = useState<PlotListItem[]>([]);

    const getAnnotationSets = async () => {
        const data = await api.get<AnnotationSet[]>(
            Endpoints.lab.experiment.annotations.base({ experimentId: experiment.uuid }),
        );
        setAnnotationSets(data);
    };

    useEffect(() => {
        if (plotListItems && plotListItems.length > 0 && !loading) {
            // Default to all analyses selected
            const initialSelectedState: Record<string, boolean> = {};
            const itemsToShow = plotListItems.filter((analysis) => analysis.name);
            itemsToShow.forEach((analysis) => {
                initialSelectedState[analysis.uuid] = true;
            });
            setAnalysesRan(itemsToShow);
            setSelectedAnalyses(initialSelectedState);
            setExpandedAnalyses(initialSelectedState);
            getAnnotationSets();
        }
    }, [plotListItems, loading]);

    useEffect(() => {
        if (pipelineMethodsSupported) {
            setPipelineMethodsExpanded(true);
            setPipelineMethodsSelected(true);
        }
    }, [pipelineMethodsSupported]);

    const generateExperimentMethods = async () => {
        if (!analysesRan.length) return;

        setGeneratingMethods(true);
        setGenerationError(null);

        try {
            // Create hidden div to render all methods
            const hiddenMethodsContainer = document.createElement('div');
            hiddenMethodsContainer.style.position = 'absolute';
            hiddenMethodsContainer.style.left = '-9999px';
            hiddenMethodsContainer.style.top = '-9999px';
            document.body.appendChild(hiddenMethodsContainer);

            // Check if any methods are selected
            const selectedAnalysesArray = analysesRan.filter((analysis) => selectedAnalyses[analysis.uuid]);
            const anyMethodsSelected =
                selectedAnalysesArray.length > 0 || (pipelineMethodsSupported && pipelineMethodsSelected);
            if (!anyMethodsSelected) {
                throw new Error('No methods selected. Please select at least one analysis or pipeline methods.');
            }

            // Render selected analyses methods in the hidden container
            const methodsPromises = selectedAnalysesArray.map(async (analysis) => {
                const methodsDiv = document.createElement('div');
                methodsDiv.id = `hidden-methods-${analysis.uuid}`;
                hiddenMethodsContainer.appendChild(methodsDiv);

                // Create a temporary React root to render the methods
                const root = document.createElement('div');
                methodsDiv.appendChild(root);

                // Use ReactDOM to render the methods content
                try {
                    const element = React.createElement(AnalysisFullMethodsContent, {
                        experiment,
                        plotId: analysis.uuid,
                        skipAnnotationFetch: true,
                        annotationSets,
                    });

                    // Use createRoot instead of ReactDOM.render for React 18 compatibility
                    const reactRoot = createRoot(root);
                    reactRoot.render(element);

                    // Wait a moment for rendering to complete
                    await new Promise((resolve) => setTimeout(resolve, 700));

                    // Extract the text content
                    const methodsText = methodsDiv.innerText || '';
                    return {
                        name: analysis.name || `Analysis ${analysis.uuid.substring(0, 8)}`,
                        text: methodsText,
                    };
                } catch (err) {
                    logger.error(`Error rendering methods for analysis ${analysis.uuid}:`, err);
                    return {
                        name: analysis.name || `Analysis ${analysis.uuid.substring(0, 8)}`,
                        text: 'No methods available',
                    };
                }
            });

            // Wait for all methods to be rendered and extracted
            const methodsResults = await Promise.all(methodsPromises);

            // Clean up the hidden container
            document.body.removeChild(hiddenMethodsContainer);

            // Format the methods texts
            const methodsTexts = methodsResults
                .filter((result) => result.text.trim().length > 0)
                .map((result) => `## ${result.name}\n\n${result.text}`);

            // Add pipeline methods if selected - use the same approach as for analysis methods
            if (pipelineMethodsSupported && pipelineMethodsSelected) {
                logger.debug('Pipeline methods are supported and selected');

                // Create a hidden div for pipeline methods
                const pipelineMethodsDiv = document.createElement('div');
                pipelineMethodsDiv.id = 'hidden-pipeline-methods';
                hiddenMethodsContainer.appendChild(pipelineMethodsDiv);

                // Create a temporary React root
                const root = document.createElement('div');
                pipelineMethodsDiv.appendChild(root);

                try {
                    // Use ReactDOM to render the pipeline methods content
                    const element = React.createElement(PipelineMethodsView, {
                        experiment,
                    });

                    const reactRoot = createRoot(root);
                    reactRoot.render(element);

                    // Wait longer for pipeline methods to load
                    await new Promise((resolve) => setTimeout(resolve, 1000));

                    // Extract the text content
                    const pipelineMethodsText = pipelineMethodsDiv.innerText || '';
                    logger.debug('Pipeline methods text:', {
                        length: pipelineMethodsText?.length || 0,
                        preview: pipelineMethodsText?.substring(0, 100),
                        hasLoadingText: pipelineMethodsText.includes('Loading'),
                    });

                    if (pipelineMethodsText.trim().length > 0 && !pipelineMethodsText.includes('Loading')) {
                        methodsTexts.unshift(`## Pipeline Methods\n\n${pipelineMethodsText}`);
                        logger.debug('Added pipeline methods to AI prompt');
                    } else {
                        logger.warn('Pipeline methods text was empty or contained "Loading"');
                    }
                } catch (err) {
                    logger.error('Error rendering pipeline methods:', err);
                }
            } else {
                logger.debug('Pipeline methods are not supported or not selected', {
                    supported: pipelineMethodsSupported,
                    selected: pipelineMethodsSelected,
                });
            }

            if (methodsTexts.length === 0) {
                throw new Error('No methods content available for any selected analysis.');
            }

            // Log the methods texts that will be sent to the AI
            logger.debug('Methods texts to be sent to AI:', {
                count: methodsTexts.length,
                titles: methodsTexts.map((text) => {
                    const match = text.match(/^## (.+)/);
                    return match ? match[1] : 'Unknown';
                }),
            });

            // Create a prompt for Gemini
            const prompt = `You are a scientific expert specializing in writing polished methods sections for biology-related experiments, with a focus on advanced sequencing techniques such as Bulk or Single Cell RNA-seq, CUT&RUN, ChIP-seq, PRO-seq, and ATAC-seq. Your task is to transform a list of auto-generated methods text into a publication-ready format that could appear in high-impact journals like Nature. You ensure clarity, precision, and conciseness while retaining all necessary experimental details. Methods are written in the past tense and organized under short, bold headings that describe each method used. You reorganize the provided input to describe all unique analyses without redundancy, condense repetitive information, and move all citations to a dedicated References section at the end, ensuring there are no duplicates. You write with scientific rigor and align your output with the conventions of professional scientific writing. Please return your answer with markdown formatting.

                Here are all of the methods sections from my experiment in Pluto for you to revise and reformat:

                ${methodsTexts.join('\n\n---\n\n')}

                Return a formatted methods and references section in markdown format. Use <sup></sup> tags for references. Do not include any additional text or explanations. The output should be a single markdown string with no extra formatting or comments.
            `;

            // Call the Gemini API with text format
            const response = await fetch('/api/vertex/vertex-gemini', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/plain, application/json',
                },
                body: JSON.stringify({
                    prompt,
                    model: 'gemini-1.5-pro',
                    responseFormat: 'text',
                    outputFormat: 'text',
                }),
            });

            const data = await response.json();

            logger.debug('Gemini API response:', data);

            // Log the full response for debugging
            logger.debug('Full Gemini API response data:', JSON.stringify(data));

            const methodsText = extractMethodsTextFromResponse(data);

            // Log the extracted methods text
            logger.debug('Extracted methods text:', {
                length: methodsText?.length || 0,
                preview: methodsText?.substring(0, 100),
            });

            setGeneratedMethods(methodsText);
        } catch (err) {
            console.error('Error generating methods:', err);
            setGenerationError('Failed to generate experiment methods');
        } finally {
            setGeneratingMethods(false);
        }
    };

    const toggleAnalysisExpand = (analysisId: string) => {
        logger.debug('Toggling analysis expand state for:', analysisId);
        setExpandedAnalyses((prev) => ({
            ...prev,
            [analysisId]: !prev[analysisId],
        }));
    };

    const resetGeneratedMethods = () => {
        setGeneratedMethods(null);
        setGenerationError(null);
    };

    const toggleAnalysisSelection = (analysisId: string) => {
        setSelectedAnalyses((prev) => ({
            ...prev,
            [analysisId]: !prev[analysisId],
        }));
        resetGeneratedMethods();
    };

    const togglePipelineMethodsSelection = () => {
        setPipelineMethodsSelected((prev) => !prev);
        resetGeneratedMethods();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogCloseButton onClose={onClose} />

            <PlutoDialogTitle title="Experiment Methods" />

            <DialogContent>
                <div className="grid grid-cols-2 gap-6 h-full">
                    {/* Left side - Analysis Methods */}
                    <div className="border-r pr-6">
                        <div className="flex flex-col justify-between mb-4 gap-1">
                            <h3 className="text-lg font-semibold">Analysis Methods</h3>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                        if (
                                            Object.values(expandedAnalyses).filter((a) => !!a).length ===
                                            analysesRan?.length
                                        ) {
                                            setExpandedAnalyses((prev) => {
                                                const newState: Record<string, boolean> = {};
                                                Object.keys(prev).forEach((key) => {
                                                    newState[key] = false;
                                                });
                                                return newState;
                                            });
                                        } else {
                                            setExpandedAnalyses((prev) => {
                                                const newState: Record<string, boolean> = {};
                                                analysesRan.forEach((analysis) => {
                                                    newState[analysis.uuid] = true;
                                                });
                                                return { ...prev, ...newState };
                                            });
                                        }

                                        if (
                                            (pipelineMethodsSupported && pipelineMethodsExpanded) ||
                                            !pipelineMethodsSupported
                                        ) {
                                            setPipelineMethodsExpanded(false);
                                        } else {
                                            setPipelineMethodsExpanded(true);
                                        }
                                    }}
                                >
                                    Expand/Collapse all
                                </Button>
                                <Button
                                    variant="text"
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                        if (
                                            Object.values(selectedAnalyses).filter((a) => !!a).length ===
                                            analysesRan?.length
                                        ) {
                                            setSelectedAnalyses((prev) => {
                                                const newState: Record<string, boolean> = {};
                                                Object.keys(prev).forEach((key) => {
                                                    newState[key] = false;
                                                });
                                                return newState;
                                            });
                                        } else {
                                            setSelectedAnalyses((prev) => {
                                                const newState: Record<string, boolean> = {};
                                                analysesRan.forEach((analysis) => {
                                                    newState[analysis.uuid] = true;
                                                });
                                                return { ...prev, ...newState };
                                            });
                                        }

                                        if (
                                            (pipelineMethodsSupported && pipelineMethodsSelected) ||
                                            !pipelineMethodsSupported
                                        ) {
                                            setPipelineMethodsSelected(false);
                                        } else {
                                            setPipelineMethodsSelected(true);
                                        }
                                    }}
                                >
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlankIcon className="text-primary text-sm" />}
                                        checkedIcon={<CheckBoxIcon className="text-sm" />}
                                        style={{ marginRight: 8, padding: 0 }}
                                        checked={
                                            Object.values(selectedAnalyses).filter((a) => !!a).length ===
                                                analysesRan?.length &&
                                            pipelineMethodsSupported &&
                                            pipelineMethodsSelected
                                        }
                                        size="small"
                                    />
                                    Select/Deselect all
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <LoadingMessage message="Loading analysis methods..." />
                        ) : error ? (
                            <div className="text-red-500">
                                {error}
                                <div className="text-xs mt-2">Error details: {JSON.stringify({ error, loading })}</div>
                            </div>
                        ) : !analysesRan || analysesRan.length === 0 ? (
                            <div>
                                <p>No analyses found for this experiment.</p>
                                <p className="text-sm text-gray-500 mt-2">Experiment ID: {experiment?.uuid}</p>
                            </div>
                        ) : (
                            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                                <div className="text-xs text-gray-500 mb-2">
                                    Found <span className="font-semibold">{analysesRan?.length}</span> analyses ran{' '}
                                    {pipelineMethodsSupported ? '+ pipeline methods' : ''}
                                </div>

                                {/* Pipeline Methods */}
                                {pipelineMethodsSupported && (
                                    <Accordion expanded={pipelineMethodsExpanded} sx={styles.accordion}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMore color="secondary" />}
                                            sx={styles.accordionSummary}
                                            onClick={() => setPipelineMethodsExpanded((prev) => !prev)}
                                        >
                                            <div className="flex items-center">
                                                <Checkbox
                                                    size="small"
                                                    checked={pipelineMethodsSelected}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        togglePipelineMethodsSelection();
                                                    }}
                                                    className="mr-2"
                                                />
                                                <h4 className="font-medium">Pipeline Methods</h4>
                                            </div>
                                        </AccordionSummary>

                                        <AccordionDetails sx={styles.accordionDetails}>
                                            <PipelineMethodsContent experiment={experiment} />
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Analysis Methods */}
                                {analysesRan.length > 0 &&
                                    analysesRan.map((analysis) => (
                                        <Accordion
                                            key={analysis.uuid}
                                            expanded={!!expandedAnalyses[analysis.uuid]}
                                            sx={styles.accordion}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMore color="secondary" />}
                                                sx={styles.accordionSummary}
                                                onClick={() => toggleAnalysisExpand(analysis.uuid)}
                                            >
                                                <div className="flex items-center">
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedAnalyses[analysis.uuid] || false}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleAnalysisSelection(analysis.uuid);
                                                        }}
                                                        className="mr-2"
                                                    />
                                                    <h4 className="font-medium">
                                                        {analysis.name || `Analysis ${analysis.uuid.substring(0, 8)}`}
                                                    </h4>
                                                </div>
                                            </AccordionSummary>

                                            <AccordionDetails sx={styles.accordionDetails}>
                                                {expandedAnalyses[analysis.uuid] && (
                                                    <AnalysisFullMethodsContent
                                                        annotationSets={annotationSets}
                                                        experiment={experiment}
                                                        plotId={analysis.uuid}
                                                        skipAnnotationFetch
                                                    />
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                            </div>
                        )}
                    </div>

                    {/* Right side - Generated Experiment Methods */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Experiment Methods</h3>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={generateExperimentMethods}
                                disabled={
                                    loading ||
                                    generatingMethods ||
                                    !analysesRan ||
                                    analysesRan.length === 0 ||
                                    (Object.values(selectedAnalyses).filter((a) => !!a).length === 0 &&
                                        !pipelineMethodsSelected)
                                }
                            >
                                {generatingMethods ? 'Generating...' : 'Generate Methods'}
                            </Button>
                        </div>

                        <div className="flex flex-col">
                            <div className="border rounded-md p-4 min-h-[60vh] max-h-[60vh] overflow-y-auto mb-4">
                                {generatingMethods ? (
                                    <LoadingMessage message="Generating comprehensive methods..." />
                                ) : generationError && !generatedMethods ? (
                                    <div className="text-red-500">{generationError}</div>
                                ) : generatedMethods ? (
                                    <div ref={generatedMethodsRef}>
                                        <PlutoMarkdownContentView
                                            content={
                                                typeof generatedMethods === 'string' &&
                                                generatedMethods.trim().length > 0
                                                    ? generatedMethods
                                                    : 'No methods content available'
                                            }
                                            className="prose prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-6 prose-h2:mb-4 prose-h2:text-gray-900 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2 
                                                prose-h3:text-lg prose-h3:font-semibold prose-h3:mt-5 prose-h3:mb-2 prose-h3:text-gray-700
                                                prose-p:mb-3 prose-p:leading-relaxed
                                                prose-ul:ml-4 prose-ul:mb-4 prose-ol:ml-4 prose-ol:mb-4
                                                prose-li:mb-2
                                                prose-blockquote:border-l-4 prose-blockquote:border-gray-200 prose-blockquote:pl-4 prose-blockquote:mx-0 prose-blockquote:italic prose-blockquote:text-gray-600
                                                prose-code:bg-gray-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-mono prose-code:text-sm
                                                prose-pre:bg-gray-50 prose-pre:p-4 prose-pre:rounded prose-pre:overflow-x-auto prose-pre:mb-4
                                                prose-table:w-full prose-table:mb-4 prose-table:border-collapse
                                                prose-th:border prose-th:border-gray-200 prose-th:p-2 prose-th:text-left prose-th:bg-gray-50 prose-th:font-semibold
                                                prose-td:border prose-td:border-gray-200 prose-td:p-2 prose-td:text-left
                                                prose-hr:border-0 prose-hr:border-t prose-hr:border-gray-200 prose-hr:my-6
                                                [&_h2:last-of-type]:mt-8 [&_h2:last-of-type]:border-b-2
                                                [&_h2:last-of-type+ol]:ml-6 [&_h2:last-of-type+ol]:list-decimal
                                                [&_h2:last-of-type+ol_li]:mb-3 [&_h2:last-of-type+ol_li]:leading-normal
                                                [&_sup]:text-xs [&_sup]:ml-0.25 [&_sup]:top-[-0.5em] [&_sup]:relative
                                                [&_sup_a]:no-underline hover:[&_sup_a]:underline hover:[&_sup_a]:text-primary
                                                [&_a[href*='#cite']]:text-gray-600 [&_a[href*='#cite']]:no-underline hover:[&_a[href*='#cite']]:underline hover:[&_a[href*='#cite']]:text-primary
                                                [&_sup:has(>a)]:bg-transparent [&_sup:has(>a)]:px-0.5 [&_sup:has(>a)]:py-0.5 [&_sup:has(>a)]:rounded"
                                        />
                                    </div>
                                ) : (
                                    <div className="text-gray-500">
                                        <p className="italic mb-4">
                                            Click &quot;Generate Methods&quot; to create a comprehensive methods section
                                            for this experiment using Google Gemini AI.
                                        </p>
                                        <p className="text-sm">
                                            Note: Select which analyses to include using the checkboxes on the left. You
                                            can expand analyses to preview their methods.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {generatedMethods && (
                                <div className="flex justify-end">
                                    <Button
                                        startIcon={<CopyIcon height={16} width={16} />}
                                        color="primary"
                                        variant="contained"
                                        onClick={() => {
                                            highlightText();
                                            copyText();
                                        }}
                                    >
                                        {showCopySuccess ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ExperimentMethodsModal;
